<script lang="ts">
  import ArticlePage from '$components/pages/blog/ArticlePage.svelte';
  import type { ArticlePreview } from '$lib/utils/definitions';
  import { articles } from '$routes/blog/articles';

  const article = articles.find(
    (article) =>
      article.slug ===
      'acheter-son-logement-sans-se-ruiner-3-solutions-a-connaitre-absolument',
  ) as ArticlePreview;
</script>

<svelte:head>
  <title>{article?.title}</title>
  <meta
    name="description"
    content={article?.description} />
</svelte:head>

<ArticlePage {article}>
  <p>
    Devenir propriétaire, c'est un rêve pour beaucoup. Mais dans un contexte où
    les prix de l'immobilier s'envolent, l'accès à la propriété peut vite
    sembler hors de portée, surtout pour les ménages modestes. Heureusement, il
    existe plusieurs dispositifs d'
    <b>accession sociale à la propriété</b>
    pour vous permettre d'acheter votre logement à des conditions beaucoup plus abordables.
  </p>
  <p></p>
  <p>
    Parmi eux, le <b>Bail Réel Solidaire (BRS)</b>
    , le
    <b>Prêt à Taux Zéro (PTZ)</b>
    et le
    <b>Prêt Social Location-Accession (PSLA)</b>
    offrent des solutions concrètes, parfois cumulables, pour alléger la facture
    et vous accompagner dans votre parcours. L'objectif ? Vous donner les clés pour
    <b>mieux comprendre ces dispositifs</b>
    et identifier celui qui correspond le mieux à votre situation.
  </p>
  <p></p>
  <p>Alors, prêt à ouvrir la porte de chez vous ? C'est parti ! 🔑</p>
  <p></p>
  <h2>
    <b>
      1. Le Bail Réel Solidaire (BRS) : acheter un logement sans acheter le
      terrain
    </b>
  </h2>
  <p>
    Le <b>BRS</b>
    est un des dispositifs les plus innovants pour accéder à la propriété à moindre
    coût. En dissociant la propriété du
    <b>bâti</b>
    (votre logement) et du
    <b>foncier</b>
    (le terrain), il permet de réduire significativement le prix d'achat.
  </p>
  <p></p>
  <h3><b>Comment ça fonctionne ?</b></h3>
  <ul>
    <li>Vous achetez votre logement (appartement ou maison).</li>
    <li>
      Le terrain reste la propriété d'un <b>
        Organisme de Foncier Solidaire (OFS
      </b>
      ), qui vous le loue pour une r
      <b>edevance modique</b>
      (entre 10 cts et 3.5€/m² par mois). Résultat ? Le prix d'achat de votre logement
      diminue, parfois jusqu'à
      <b>40%</b>
      par rapport aux prix du marché !
    </li>
  </ul>
  <p></p>
  <h3><b>Pour qui ?</b></h3>
  <p>
    Le BRS s'adresse aux ménages dont les <b>revenus</b>
    ne dépassent pas certains
    <b>plafonds</b>
    fixés par la réglementation (en fonction de la composition de votre foyer et
    de la zone géographique). C'est un dispositif pensé pour favoriser l'
    <b>accession sociale à la propriété</b>
    , en particulier dans les zones où les prix de l'immobilier sont très tendus.
  </p>
  <p></p>
  <h3><b>Les avantages concrets :</b></h3>
  <ul>
    <li>
      <b>Un prix d'achat réduit</b>
      : idéal pour les ménages qui peinent à acheter au prix du marché.
    </li>
    <li>
      <b>Des frais annexes allégés</b>
      : TVA réduite à
      <b>5,5%</b>
      et
      <b>abattement de taxe foncière</b>
      dans certaines communes (ce n'est pas forcément le cas).
    </li>
    <li>
      <b>Un dispositif solidaire</b>
      : en cas de revente, le logement reste abordable pour les futurs acheteurs,
      car son prix est plafonné.
    </li>
  </ul>
  <p></p>
  <p>
    Vous souhaitez savoir si vous êtes éligible ? C'est <a
      href="/simulateur-eligibilite"
      class="fr-link">
      par ici
    </a>
    que ça se passe.
  </p>
  <h2>
    <b>
      2. Le Prêt à Taux Zéro (PTZ) : un gros coup de pouce pour financer votre
      achat
    </b>
  </h2>
  <p>
    Le <b>PTZ</b>
    est un prêt sans intérêt destiné à compléter un financement immobilier pour les
    <b>primo-accédants</b>
    (les personnes qui achètent pour la première fois ou n'ont pas été propriétaires
    depuis au moins 2 ans).
  </p>
  <h3><b>Comment ça fonctionne ?</b></h3>
  <ul>
    <li>
      Le PTZ ne finance qu'une partie de votre achat (jusqu'à <b>50%</b>
      du prix du logement).
    </li>
    <li>
      Il est attribué sous <b>conditions de ressources</b>
      et dépend de la
      <b>zone géographique</b>
      du bien (zones A, B1, B2, C - pour connaître votre zone, rendez-vous
      <a
        href="https://www.service-public.fr/simulateur/calcul/zones-tendues"
        class="fr-link"
        target="_blank">
        ici
      </a>
      ).
    </li>
    <li>
      Le remboursement du PTZ peut être différé de <b>5 à 15 ans</b>
      , ce qui vous permet de
      <b>souffler un peu financièrement</b>
      au début de votre achat. Le lissage PTZ + prêt immobilier offre la possibilité
      d'avoir la même mensualité pendant toute la durée des deux prêts.
    </li>
  </ul>
  <h3><b>Pour qui ?</b></h3>
  <p>
    Le PTZ est réservé aux ménages dont les revenus sont inférieurs aux <b>
      plafonds réglementaires
    </b>
    , avec des variations selon la taille du foyer et la localisation du bien. Il
    est particulièrement avantageux dans le neuf, mais peut aussi financer certains
    logements anciens, à condition de réaliser des
    <b>travaux importants</b>
    .
  </p>
  <h3><b>Les avantages concrets :</b></h3>
  <ul>
    <li>
      <b>Un prêt sans intérêt</b>
      : vous remboursez uniquement le montant emprunté, sans frais supplémentaires.
    </li>
    <li>
      <b>Un différé de remboursement</b>
      : vous pouvez commencer à rembourser plus tard, lorsque votre situation financière
      sera plus stable.
    </li>
    <li>
      <b>Cumulable avec d'autres dispositifs</b>
      : le PTZ peut être associé au
      <b>BRS</b>
      ou au
      <b>PSLA</b>
      , pour encore plus d'économies.
    </li>
  </ul>
  <p>
    En bref, le PTZ, c'est un véritable coup de pouce pour devenir propriétaire
    sans alourdir votre budget dès le départ.
  </p>
  <p>
    Rendez-vous <a
      href="https://www.calcul-ptz.fr/lissage.html"
      class="fr-link"
      target="_blank">
      ici
    </a>
    pour lisser les mensualités du PTZ avec votre prêt immobilier.
  </p>
  <h2>
    <b>
      3. Le Prêt Social Location-Accession (PSLA) : un achat progressif, en
      toute sécurité
    </b>
  </h2>
  <p>
    Vous aimeriez tester la vie dans votre futur logement avant de l'acheter ?
    Le <b>Prêt Social Location-Accession (PSLA)</b>
    est fait pour vous. Ce dispositif fonctionne en deux étapes :
  </p>
  <h3><b>Comment ça marche ?</b></h3>
  <p>
    <b>1. Phase locative :</b>
    Vous commencez par louer le logement pendant une période définie, tout en ayant
    une promesse de vente au prix fixé dès le départ. Une partie du montant à payer
    chaque mois constitue une épargne pour constituer un apport.
  </p>
  <p>
    <b>2. Phase d'acquisition :</b>
    À la fin de cette période, vous avez la possibilité d'acheter le logement, tout
    en bénéficiant de conditions avantageuses et de certains avantages fiscaux.
  </p>
  <h3><b>Pour qui ?</b></h3>
  <p>
    Le PSLA est réservé aux ménages dont les revenus respectent des plafonds,
    les mêmes que ceux du BRS et du PTZ. Il s'adresse particulièrement à ceux
    qui veulent accéder à la propriété <b>progressivement</b>
    , tout en sécurisant leur projet.
  </p>
  <h3><b>Les avantages concrets :</b></h3>
  <ul>
    <li>
      <b>Tester avant d'acheter</b>
      : Vous voyez si le logement correspond vraiment à vos besoins.
    </li>
    <li>
      <b>Un prix qui évolue avec le temps :</b>
      le coût total est fixé à l'avance, mais il est progressivement décoté au fil
      des années de location, vous offrant une opportunité d'achat encore plus avantageuse.
    </li>
    <li>
      <b>Des aides supplémentaires</b>
      : TVA réduite à 5,5 % et exonération de taxe foncière pendant 15 ans.
    </li>
  </ul>
  <p>
    Le PSLA, c'est l'assurance d'un achat en douceur, surtout si vous hésitez à
    franchir le pas tout de suite.
  </p>
  <h2>
    <b>4. Les aides locales et dispositifs complémentaires</b>
  </h2>
  <p>
    En plus des dispositifs nationaux comme le BRS, le PTZ ou le PSLA, certaines <b>
      aides locales
    </b>
    peuvent faire une grande différence dans votre budget.
  </p>
  <h3><b>Exemples d'aides complémentaires :</b></h3>
  <ul>
    <li>
      <b>TVA réduite à 5,5 %</b>
      : Disponible pour certains logements neufs en zones prioritaires ou sous conditions
      de ressources, cette TVA réduite peut alléger considérablement le coût de votre
      achat.
    </li>
    <li>
      <b>Exonération de taxe foncière</b>
      : Certaines communes peuvent offrir un abattement pendant plusieurs années,
      mais ce n'est pas fle cas partout.
    </li>
    <li>
      <b>Aides des collectivités locales</b>
      : Certaines régions, départements ou communes proposent des subventions ou
      des prêts complémentaires pour les primo-accédants. Renseignez-vous auprès
      de votre mairie ou conseil régional.
    </li>
  </ul>
  <p>
    Ces dispositifs viennent en renfort des aides principales, pour rendre votre
    projet encore plus abordable.
  </p>
  <h2>
    <b>À vous de jouer pour devenir propriétaire !</b>
  </h2>
  <p>
    Devenir propriétaire n'a jamais été un chemin simple, mais grâce aux
    dispositifs d'accession sociale à la propriété, ce rêve devient accessible à
    de nombreux ménages. Que vous optiez pour le <b>
      Bail Réel Solidaire (BRS)
    </b>
    pour alléger le coût d'achat, le
    <b>Prêt à Taux Zéro (PTZ)</b>
    pour réduire vos mensualités ou encore le
    <b>Prêt Social Location-Accession (PSLA)</b>
    pour tester votre achat en douceur, chaque solution a été pensée pour vous accompagner.
  </p>
  <p>
    Et ce n'est pas tout : avec des aides locales comme la <b>TVA réduite</b>
    , le
    <b>PTZ</b>
    ou des exonérations de taxe foncière, ces dispositifs peuvent même se cumuler
    pour maximiser vos avantages.
  </p>
  <p>
    Vous avez désormais toutes les clés en main pour explorer ces solutions et
    voir ce qui correspond le mieux à votre projet. Alors, pourquoi ne pas
    commencer par vous renseigner sur les <a
      href="/logements-brs-disponibles"
      class="fr-link">
      logements disponibles
    </a>
    en
    <b>Bail Réel Solidaire</b>
    près de chez vous ?
  </p>
</ArticlePage>
